import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ExternalLink, Download, FileText } from 'lucide-react';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { Button } from '@/components/ui/button';

interface TermsPDFViewerProps {
  termsUrl: string;
}

const TermsPDFViewer: React.FC<TermsPDFViewerProps> = ({ termsUrl }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  
  const { signedUrl } = useSignedUrl(termsUrl, 'gym-documents');

  // Add timeout to show fallback if PDF doesn't load
  useEffect(() => {
    if (signedUrl && isLoading) {
      const timeout = setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
          setShowFallback(true);
        }
      }, 8000); // Reduced to 8 seconds for faster fallback

      return () => clearTimeout(timeout);
    }
  }, [signedUrl, isLoading]);

  // Add effect to detect if we're in a secure context
  useEffect(() => {
    // Check if browser supports PDF viewing in iframes
    const supportsPDFViewing = (() => {
      // Chrome often blocks PDF iframes, detect this
      const isChrome = /Chrome/.test(navigator.userAgent);
      const isSecureContext = window.isSecureContext;
      
      return !isChrome || isSecureContext;
    })();

    if (!supportsPDFViewing && signedUrl) {
      console.log('Browser may not support PDF iframe viewing, preparing fallback');
    }
  }, [signedUrl]);

  const handleLoad = () => {
    console.log('📄 PDF iframe loaded successfully');
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    console.error('❌ PDF iframe failed to load');
    setIsLoading(false);
    setHasError(true);
    setShowFallback(true);
  };

  // Add iframe ref to detect loading issues
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Enhanced iframe load detection
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !signedUrl) return;

    const checkIframeLoad = () => {
      try {
        // Try to access iframe content to detect if it's blocked
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) {
          console.warn('⚠️ Cannot access iframe content - may be blocked');
          return false;
        }
        return true;
      } catch (error) {
        console.warn('⚠️ Iframe access blocked:', error);
        return false;
      }
    };

    // Check after a brief delay to allow iframe to start loading
    const checkTimeout = setTimeout(() => {
      if (isLoading && !checkIframeLoad()) {
        console.log('🔄 Iframe seems blocked, showing fallback');
        setIsLoading(false);
        setShowFallback(true);
      }
    }, 3000); // Check after 3 seconds

    return () => clearTimeout(checkTimeout);
  }, [signedUrl, isLoading]);

  const openPDFInNewTab = () => {
    if (signedUrl) {
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderFallbackOptions = () => (
    <div className="flex flex-col items-center justify-center gap-4 p-8">
      <FileText className="h-12 w-12 text-muted-foreground" />
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-4">
          {t('clients.termsAcceptance.pdfViewerBlocked')}
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={openPDFInNewTab}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            {t('clients.termsAcceptance.openInNewTab')}
          </Button>
          <a
            href={signedUrl}
            download
            className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
          >
            <Download className="h-4 w-4" />
            {t('clients.termsAcceptance.downloadPDF')}
          </a>
        </div>
      </div>
    </div>
  );

  if (!signedUrl) {
    return (
      <div className="flex items-center justify-center h-96 bg-muted rounded-lg">
        <div className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm text-muted-foreground">
            {t('clients.termsAcceptance.loadingTerms')}
          </span>
        </div>
      </div>
    );
  }

  if (hasError || showFallback) {
    return (
      <div className="flex items-center justify-center h-96 bg-muted rounded-lg">
        {renderFallbackOptions()}
      </div>
    );
  }

  return (
    <div className="relative h-96 md:h-[500px] w-full bg-muted rounded-lg overflow-hidden">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm text-muted-foreground">
              {t('clients.termsAcceptance.loadingTerms')}
            </span>
          </div>
        </div>
      )}
      
      {/* Try to render PDF in iframe with enhanced settings */}
      <iframe
        ref={iframeRef}
        src={`${signedUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH&zoom=page-fit`}
        className="w-full h-full border-0"
        title={t('gymSettings.terms')}
        onLoad={handleLoad}
        onError={handleError}
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-presentation"
        allow="fullscreen"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        style={{ 
          background: 'transparent',
          colorScheme: 'light dark'
        }}
      />
      
      {/* Fallback button overlay - shows after a delay if iframe fails to load */}
      <div className="absolute top-2 right-2 z-20">
        <Button
          onClick={openPDFInNewTab}
          variant="outline"
          size="sm"
          className="bg-background/90 backdrop-blur-sm"
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          {t('clients.termsAcceptance.openExternal')}
        </Button>
      </div>
    </div>
  );
};

export default TermsPDFViewer;